// UI-2: Button. design-brief.md section 3 "Buttons"; mobile-responsive-addendum.md 2.7 (44px
// hit area on touch).
import React from 'react';
import './v2.css';

export const BUTTON_VARIANTS = ['create', 'confirm', 'secondary', 'tertiary', 'destructive', 'dark', 'delete'];

const cx = (...parts) => parts.filter(Boolean).join(' ');

// variant: create (+ New X), confirm (Save, Approve), secondary (Edit), tertiary,
// destructive (a secondary Delete on a page), dark (Stop timer), delete (the main action of a
// ConfirmDialog, decision 51: the High priority coral with ink text and an ink outline). type defaults to "button" so a Button
// inside a form never submits it by accident.
export const Button = React.forwardRef(function Button(
  { variant = 'tertiary', size, block = false, type = 'button', className, children, ...rest }, ref,
) {
  if (!BUTTON_VARIANTS.includes(variant)) throw new Error(`Button: unknown variant "${variant}"`);
  return (
    <button ref={ref} type={type} className={cx('ui-btn', `ui-btn--${variant}`, size === 'sm' && 'ui-btn--sm', block && 'ui-btn--block', className)} {...rest}>
      {children}
    </button>
  );
});

// Decision 88 (rewritten by Carla, 2026-10-21): the actions next to a PAGE TITLE are BUTTONS on every
// page. HeaderButton is the one thing they are made of: `primary` is the green create button (the
// Add task token, .ui-addtask, with ink text; marked data-green-create), at most one per header; every
// other header action is a secondary Button (marked data-header-button). Every attribute passes
// through, so a data-tour-id or data-testid a tour or a test reads is kept.
export const HeaderButton = React.forwardRef(function HeaderButton({ primary = false, className, children, ...rest }, ref) {
  if (primary) {
    return <Button ref={ref} variant="create" className={cx('ui-addtask', className)} data-green-create="" {...rest}>{children}</Button>;
  }
  return <Button ref={ref} variant="secondary" className={className} data-header-button="" {...rest}>{children}</Button>;
});

// Decision 88: actions on TILES and CARDS (a DashboardCard's strip) are text links. A text link (brand ink, Poppins
// 600, underline on hover and keyboard focus, no background or border; a 44px tap area on a phone),
// rendered as a <button> (or an <a> when it has an href). Every attribute passes through, so a
// data-tour-id or data-testid a tour or a test reads is kept.
export const HeaderAction = React.forwardRef(function HeaderAction({ href, type = 'button', className, children, ...rest }, ref) {
  const cls = cx('ui-header-action', className);
  if (href) return <a ref={ref} href={href} className={cls} {...rest}>{children}</a>;
  return <button ref={ref} type={type} className={cls} {...rest}>{children}</button>;
});

// Decision 88's exception: "+ Add task" stays a button, and EVERY Add task button in the app is
// the floating Aidey button's green (.ui-addtask: the --highlight token, ink text, 12.9:1 measured).
export const AddTaskButton = React.forwardRef(function AddTaskButton({ className, children = '+ Add task', ...rest }, ref) {
  return <Button ref={ref} variant="create" className={cx('ui-addtask', className)} {...rest}>{children}</Button>;
});

// The ⋯ OverflowMenu lives in Popover.jsx (UI-3), on the shared popover and sheet.
